import { EditOutlined } from "@ant-design/icons"
import axiosInstance from "../../../../axios/axiosInstance"
import { useEffect, useState } from "react"
import { Input, Table, Tag } from "antd"
import type { ColumnsType } from "antd/es/table/interface"
import OrderFormModal from "./OrderFormModal"
import processError from "../../../../utils/processError"
import useTableConfig from "../../../../hooks/useTableConfig"
import dayjs from "dayjs"
import {OrderResponse} from "../../../../model/response";
import {OrderState} from "../../../../model/enum";
import OrderActionsColumn from "./OrderActionsColumn";

export default function OrderManagement() {
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig()
  const [email, setEmail] = useState<string>("")
  const [orderId, setOrderId] = useState<string>("")
  const [isOpen, setOpenModal] = useState<boolean>(false)

  const [data, setData] = useState<OrderResponse[]>()
  const [loading, setLoading] = useState(false)

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(price)
  }

  const getStatusColor = (status: OrderState) => {
    return status === "PAID" ? "green" : "red"
  }

  const getStatusText = (status: OrderState) => {
    return status === "PAID" ? "Đã thanh toán" : "Chưa thanh toán"
  }

  const columns: ColumnsType<OrderResponse> = [
    {
      title: "Mã đơn hàng",
      dataIndex: "orderCode",
      width: "12%",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("orderCode"),
    },
    {
      title: "Khách hàng",
      dataIndex: "customerName",
      width: "15%",
      align: "left",
      render: (_, record) => (
        <div>
          <div className="font-medium">{record.customerName || `${record.firstName} ${record.lastName}`}</div>
          <div className="text-sm text-gray-500">{record.email}</div>
        </div>
      ),
    },
    {
      title: "User ID",
      dataIndex: "userId",
      width: "10%",
      align: "center",
      render: (userId) => (
        <div className="max-w-[80px] truncate" title={userId}>
          {userId}
        </div>
      ),
    },
    {
      title: "Tổng tiền",
      dataIndex: "totalPrice",
      width: "12%",
      align: "right",
      sorter: true,
      sortOrder: getColumnSorter("totalPrice"),
      render: (price) => formatPrice(price),
    },
    {
      title: "Giảm giá",
      dataIndex: "discountPrice",
      width: "10%",
      align: "right",
      sorter: true,
      sortOrder: getColumnSorter("discountPrice"),
      render: (price) => formatPrice(price),
    },
    {
      title: "Thành tiền",
      width: "12%",
      align: "right",
      render: (_, record) => formatPrice(record.totalPrice - record.discountPrice),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      width: "10%",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("status"),
      render: (status) => <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>,
    },
    {
      title: "Ngày tạo",
      dataIndex: "createdAt",
      width: "12%",
      align: "center",
      sorter: true,
      sortOrder: getColumnSorter("createdAt"),
      render: (date) => dayjs(date).format("DD/MM/YYYY HH:mm"),
    },
    {
      title: "Hành động",
      dataIndex: "action",
      width: "8%",
      align: "center",
      render: (_, record) => (
        <OrderActionsColumn order={record} onEdit={(e) => moveEditView(e, record)} onRefreshOrder={loadListOrders} />
      ),
    },
  ]

  const moveEditView = async (e: any, record: OrderResponse) => {
    setOpenModal(true)
    setOrderId(record.id)
  }

  const loadListOrders = async () => {
    setLoading(true)
    try {
      const res = await axiosInstance({
        url: "order/page",
        params: {
          email,
          size: pageSize,
          page: currentPage,
          sortBy,
          direction,
          fromCMS: true,
        },
      })

      if (res.status === 200) {
        setData(res.data.content)
        setTotalElements(res.data.totalElements)
      }
    } catch (err) {
      processError(err)
    }
    setLoading(false)
  }

  const onSearch = (e: any) => {
    setEmail(e.target.value)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
  }

  const handleBeforeSuccess = () => {
    setOpenModal(false)
    loadListOrders().then()
  }

  useEffect(() => {
    loadListOrders().then()
  }, [currentPage, pageSize, sortBy, direction, email])

  return (
    <div className="w-full h-[calc(100vh-112px)] overflow-y-auto p-4">
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <h2 className="text-lg font-medium">Quản lý đơn hàng</h2>
        </div>
        <Input placeholder="Nhập email để tìm kiếm" name="userId" className="w-fit" onPressEnter={onSearch} />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </div>
      <OrderFormModal
        isOpen={isOpen}
        onCloseModal={handleCloseModal}
        onBeforeSuccess={handleBeforeSuccess}
        orderId={orderId}
      />
    </div>
  )
}
